import {
  getOrganizationByEntityId,
  getObligationInPreparationById,
} from './notificationResolvers';
import { findObligationInLists } from './obligationNotificationLinks';
import {
  findOrganizationInLists,
  organizationNameFromCache,
} from './organizationNotificationUtils';

/**
 * @typedef {Object} NotificationDisplayContext
 * @property {boolean} isAdmin
 * @property {object|null} loginOrganizations
 * @property {object[]} obligations
 * @property {object[]} obligationsInPreparation
 * @property {object[]} organizations
 * @property {Record<string, string>} organizationNameById
 * @property {(id: string|number) => object|null} resolveOrganization
 * @property {(id: string|number) => object|null} resolveObligation
 * @property {Record<string, { documentId: string, noticeId: string, title: string|null, slug: string|null }>} consultationByDocumentId
 * @property {(documentId: string|number) => { documentId: string, noticeId: string, title: string|null, slug: string|null }|null} resolveConsultationByDocument
 * @property {Record<string, object>} obligationProposalById
 * @property {(proposalId: string|number) => object|null} resolveObligationProposal
 * @property {string|null} draftActionPlanId
 * @property {string|null} implementationActionPlanId
 * @property {string[]} completedActionPlanIds
 */

/**
 * @param {object} options
 * @param {boolean} [options.isAdmin]
 * @param {object|null} [options.loginOrganizations]
 * @param {object[]} [options.obligations]
 * @param {object[]} [options.obligationsInPreparation]
 * @param {object[]} [options.organizations]
 * @param {Record<string, string>} [options.organizationNameById]
 * @param {Record<string, object>} [options.consultationByDocumentId]
 * @param {string|null} [options.draftActionPlanId]
 * @param {string|null} [options.implementationActionPlanId]
 * @param {Array<string|number>} [options.completedActionPlanIds]
 * @returns {NotificationDisplayContext}
 */
export function buildNotificationContext({
  isAdmin = false,
  loginOrganizations = null,
  obligations = [],
  obligationsInPreparation = [],
  organizations = [],
  organizationNameById = {},
  consultationByDocumentId = {},
  obligationProposalById = {},
  draftActionPlanId = null,
  implementationActionPlanId = null,
  completedActionPlanIds = [],
  extraObligations = [],
} = {}) {
  const orgList = organizations || [];
  const nameById = organizationNameById || {};
  const consultationByDoc = consultationByDocumentId || {};
  const proposalById = obligationProposalById || {};
  const mergedObligations = [...(obligations || []), ...(extraObligations || [])];

  return {
    isAdmin: !!isAdmin,
    loginOrganizations,
    obligations: mergedObligations,
    obligationsInPreparation: obligationsInPreparation || [],
    draftActionPlanId: draftActionPlanId != null && draftActionPlanId !== '' ? String(draftActionPlanId) : null,
    implementationActionPlanId:
      implementationActionPlanId != null && implementationActionPlanId !== ''
        ? String(implementationActionPlanId)
        : null,
    completedActionPlanIds: (Array.isArray(completedActionPlanIds) ? completedActionPlanIds : [])
      .filter((id) => id != null && id !== '')
      .map(String),
    organizations: orgList,
    organizationNameById: nameById,
    resolveOrganization: (id) => {
      const fromLogin = getOrganizationByEntityId(loginOrganizations, id);
      if (fromLogin?.name) return fromLogin;
      const fromList = findOrganizationInLists(id, orgList);
      if (fromList) return fromList;
      const cachedName = organizationNameFromCache(id, nameById);
      if (cachedName) {
        return { id, name: cachedName };
      }
      return null;
    },
    resolveObligation: (id) => {
      const fromPrep = getObligationInPreparationById(obligationsInPreparation, id);
      if (fromPrep) return fromPrep;
      return findObligationInLists(id, mergedObligations, obligationsInPreparation);
    },
    obligationProposalById: proposalById,
    resolveObligationProposal: (proposalId) => {
      if (proposalId == null || proposalId === '') return null;
      return proposalById[String(proposalId)] ?? null;
    },
    consultationByDocumentId: consultationByDoc,
    resolveConsultationByDocument: (documentId) => {
      if (documentId == null || documentId === '') return null;
      return consultationByDoc[String(documentId)] ?? null;
    },
  };
}
